import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import { apiGetById, apiPost, apiPut } from "../../services/genesisApi/httpsMethods";
import type { ApiResponse } from "../../services/genesisApi/httpsMethods";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import type { Note } from "../../types/deal";
import { formatNoteDateTime } from "../../utils/formatDate";
import { getInitials, getColorForName } from "../../utils/noteUtils";
import { CustomMulitLineTextField } from "../../components/common/inputFields/customMultiLineTextFIeld";
import { ConfirmDialog } from "../../components/common/confirmDialog";
import { CustomButton } from "../../components/common/customButton";
import { ErrorDialog } from "../../components/common/errorDialog";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { useGlobalProvider } from "../../context/GlobalContext";
import { useCurrentUser } from "../../context/CurrentUserContext";
import pencilIcon from "@/assets/svg/edit.svg";

const RAIL_CIRCLE_SIZE = 32;

const iconButtonSx = {
  '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none', backgroundColor: 'transparent' },
  '&:focus': { outline: 'none', backgroundColor: 'transparent' },
  '&:active': { backgroundColor: 'transparent' },
  '&:hover': { backgroundColor: 'transparent' },
  '& .MuiTouchRipple-root': { display: 'none' },
};

interface NotesEditorProps {
  dealId: number;
}

const sortNotesNewestFirst = (notes: Note[]): Note[] =>
  [...notes].sort((a, b) => {
    const dateA = new Date(a.updated_at || a.created_at).getTime();
    const dateB = new Date(b.updated_at || b.created_at).getTime();
    return dateB - dateA;
  });

const NotesEditor = ({ dealId }: NotesEditorProps) => {
  const { canEdit } = useCurrentUser();
  const [editNoteId, setEditNoteId] = useState<number | null>(null);
  const [draftText, setDraftText] = useState<string>("");
  const [noteData, setNoteData] = useState<Note[]>([]);
  const [newNoteText, setNewNoteText] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  // Guards against responses landing after the deal has changed (or a newer fetch started).
  const dealIdRef = useRef(dealId);
  const fetchRequestIdRef = useRef(0);

  const { user, userLoading } = useGlobalProvider();

  const fetchNotes = useCallback(async () => {
    // A fetch captured before dealId changed must not invalidate the current deal's request.
    if (dealIdRef.current !== dealId) return;
    const requestId = ++fetchRequestIdRef.current;
    const isStale = () => requestId !== fetchRequestIdRef.current || dealIdRef.current !== dealId;
    try {
      const response = await apiGetById("deal-notes", dealId);
      if (isStale()) return;
      if (response?.success && response.data) {
        setNoteData(sortNotesNewestFirst(response.data));
      } else {
        showErrorFromResponse(response);
      }
    } catch (error) {
      if (isStale()) return;
      console.error("Error fetching notes:", error);
      showErrorFromResponse(undefined);
    }
  }, [dealId, showErrorFromResponse]);

  useEffect(() => {
    dealIdRef.current = dealId;
    // Drop everything tied to the previous deal so it can't be shown or saved against this one.
    setNoteData([]);
    setEditNoteId(null);
    setDraftText("");
    setNewNoteText("");
    void fetchNotes();
    return () => {
      fetchRequestIdRef.current += 1;
    };
  }, [dealId, fetchNotes]);

  const handleEdit = (noteId: number, currentText: string) => {
    setEditNoteId(noteId);
    setDraftText(currentText);
  };

  const handleCancel = () => {
    setEditNoteId(null);
    setDraftText("");
  };

  const handleSave = async (noteObj: Note) => {
    if (draftText === noteObj.note) return;
    if (userLoading || !user?.tenfiftyid) return;
    const savedText = draftText;
    let response: ApiResponse | undefined;
    try {
      response = await apiPut("deal-notes", noteObj.id, { note: savedText, updated_by_id: user.tenfiftyid });
    } catch (error) {
      console.error("Error updating note:", error);
    }
    if (dealIdRef.current !== dealId) return;
    if (!response?.success) {
      // Keep the draft and edit mode so the user can retry.
      showErrorFromResponse(response);
      return;
    }
    setNoteData((prev) =>
      sortNotesNewestFirst(
        prev.map((note) =>
          note.id === noteObj.id ? { ...note, note: savedText, updated_at: new Date().toISOString() } : note
        )
      )
    );
    setDraftText("");
    setEditNoteId(null);
  };

  const handleCreate = async () => {
    if (dealId == null || typeof dealId !== 'number' || dealId < 1) return;
    if (!newNoteText.trim() || userLoading || !user?.tenfiftyid) return;
    let response: ApiResponse | undefined;
    try {
      response = await apiPost(`deal-notes/${dealId}`, { note: newNoteText, created_by_id: user.tenfiftyid });
    } catch (error) {
      console.error("Error creating note:", error);
    }
    if (dealIdRef.current !== dealId) return;
    if (!response?.success) {
      showErrorFromResponse(response);
      return;
    }
    setNewNoteText("");
    await fetchNotes();
  };

  const handleDelete = (noteObj: Note) => {
    setNoteToDelete(noteObj);
    setDialogOpen(true);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", backgroundColor: "#f2f5f9", marginBottom: 2 }}>
      {/* Add a note: full-width box, actions underneath */}
      <Box sx={{ mb: 2.5, "& .MuiInputBase-root": { fontSize: "0.8125rem", backgroundColor: "#fff" }, "& .MuiInputLabel-root": { fontSize: "0.8125rem" } }}>
        <CustomMulitLineTextField
          label="Add a note"
          value={newNoteText}
          onChange={(val) => setNewNoteText(String(val))}
          margin="dense"
          delay={0}
        />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, mt: 1 }}>
          {userLoading && <CircularProgress size={14} />}
          {newNoteText && (
            <CustomButton
              handleButtonPress={() => setNewNoteText("")}
              isButtonDisabled={false}
              text="Cancel"
              solid={false}
              heightOverride="28px"
              sx={{ fontSize: "13px !important" }}
            />
          )}
          <CustomButton
            handleButtonPress={() => void handleCreate()}
            isButtonDisabled={!canEdit || userLoading || !user?.tenfiftyid || !newNoteText.trim()}
            text="Add note"
            heightOverride="28px"
            sx={{ fontSize: "13px !important" }}
          />
        </Box>
      </Box>

      <ConfirmDialog
        open={dialogOpen}
        endpoint="deal-notes"
        id={noteToDelete?.id || 0}
        title="Delete Note"
        description="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onClose={() => setDialogOpen(false)}
        onSuccess={() => {
          fetchNotes();
          setNoteToDelete(null);
        }}
      />

      {/* Timeline rail */}
      <Box sx={{ position: "relative", pl: 0 }}>
        {noteData.map((noteObj, index) => {
          const initials = getInitials(noteObj.created_by_name);
          const bgColor = getColorForName(noteObj.created_by_name);
          const isEditing = editNoteId === noteObj.id;
          return (
            <Box
              key={noteObj.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                position: "relative",
                pb: 2,
                "&:last-of-type": { pb: 0 },
              }}
            >
              {/* Rail line (vertical) - from top of circle to bottom of row */}
              {index < noteData.length - 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    left: RAIL_CIRCLE_SIZE / 2 - 1,
                    top: RAIL_CIRCLE_SIZE,
                    bottom: 0,
                    width: 2,
                    backgroundColor: "divider",
                    zIndex: 0,
                  }}
                />
              )}
              {/* Circle with initials */}
              <Box
                sx={{
                  width: RAIL_CIRCLE_SIZE,
                  height: RAIL_CIRCLE_SIZE,
                  borderRadius: "50%",
                  backgroundColor: bgColor,
                  color: "#FFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: "0.75rem",
                  zIndex: 1,
                }}
              >
                {initials}
              </Box>
              {/* Content: note text above, then name & date + edit/delete inline */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {isEditing ? (
                  <CustomMulitLineTextField label="Edit Note" value={draftText} onChange={(val) => setDraftText(String(val))} />
                ) : (
                  <Typography variant="body1" color="#182737 !important" sx={{ whiteSpace: "pre-wrap", fontSize: "14px !important", mb: 0.25 }}>
                    {noteObj.note}
                  </Typography>
                )}
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ color: "#d3d3d3", fontSize: "12px !important" }}>
                  <Typography component="span" sx={{ color: "#adadad !important", fontSize: "0.6875rem !important" }}>
                    {noteObj.created_by_name}
                  </Typography>
                  <Typography component="span" sx={{ color: "#adadad !important", fontSize: "0.6875rem !important" }}>
                    {formatNoteDateTime(noteObj.created_at)}
                  </Typography>
                  {(isEditing || String(noteObj.created_by_id) === String(user?.tenfiftyid ?? "")) && (
                    <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 0 }}>
                      {isEditing ? (
                        <>
                          <IconButton onClick={() => handleSave(noteObj)} size="small" disableRipple sx={{ ...iconButtonSx, color: "#d3d3d3", p: 0.25 }} disabled={!canEdit || userLoading || !user?.tenfiftyid}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancel} size="small" disableRipple sx={{ ...iconButtonSx, color: "#d3d3d3", p: 0.25 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton onClick={() => handleEdit(noteObj.id, noteObj.note)} size="small" disableRipple sx={{ ...iconButtonSx, color: "#d3d3d3", p: 0.25 }} disabled={!canEdit || userLoading}>
                            <Box
                              component="img"
                              src={pencilIcon}
                              alt="Edit note"
                              sx={{ width: 16, height: 16, display: "block" }}
                            />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(noteObj)} size="small" disableRipple sx={{ ...iconButtonSx, color: "#d3d3d3", p: 0.25 }} disabled={!canEdit}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default NotesEditor;
